import mixpanel from 'mixpanel-browser';
import { MixpanelInitializer } from '@/modules/mixpanelInitializer';

interface ReportBuilderQueryData {
  queryText: string;
  // Response-related fields (optional - only available after API response)
  measure?: string;
  dimension?: string;
  filters?: any[];
  columns?: string[];
  dateRange?: {
    period?: string;
    from?: string;
    to?: string;
  };
  confidenceScore?: number;
  processingTimeMs?: number;
  status?: 'success' | 'error';
  errorMessage?: string;
  // Agent integration fields
  agentId?: string;
  agentName?: string;
  conversationType?: 'direct_query' | 'conversational' | 'clarification';
  messageNumber?: number;
  sessionId?: string;
  isRefinement?: boolean;
  previousQueryId?: string;
}

interface ReportSaveData {
  customReportId: string;
  reportName: string;
  timeToSaveSeconds: number;
  modificationsMode: boolean;
}

// New interfaces for agent integration
interface AgentConversationData {
  agentId: string;
  sessionId: string;
  messageCount: number;
  clarificationRequestsCount: number;
  successfulInterpretation: boolean;
  conversationDuration: number;
  queryRefinementIterations: number;
}

interface AgentResponseData {
  responseType: 'report_generation' | 'greeting' | 'help' | 'clarification' | 'unclear';
  confidenceScore?: number;
  requiresUserAction: boolean;
  processingTimeMs: number;
  messageNumber: number;
}

interface ChatboxStateData {
  state: 'opened' | 'closed' | 'tab_switched';
  source?: string;
  duration?: number;
  interactionCount?: number;
  fromTab?: string;
  toTab?: string;
}

interface AgentSelectionData {
  agentId: string;
  agentName: string;
  selectionMethod: 'auto' | 'manual' | 'page_context';
  previousAgent?: string;
  isNewConversation: boolean;
}

interface QueryRefinementData {
  originalQuery: string;
  refinedQuery: string;
  refinementReason: 'low_confidence' | 'missing_info' | 'user_initiated';
  iterationNumber: number;
  previousConfidenceScore?: number;
  timeToRefinement: number;
}

export class ReportBuilderEventHandler {
  // PII detection patterns - shared across sanitization and validation
  private static readonly CREDIT_CARD_PATTERN = /\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/g;
  private static readonly SSN_PATTERN = /\b\d{3}-\d{2}-\d{4}\b/g;
  private static readonly EMAIL_PATTERN = /[\w.-]+@[\w.-]+\.\w+/g;
  private static readonly PHONE_PATTERN_1 = /\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g;
  private static readonly PHONE_PATTERN_2 = /\(\d{3}\)\s*\d{3}[-.]?\d{4}/g;

  private sessionStartTime: number;
  private sessionId: string;
  private queryStartTime: number;
  private reportGeneratedTime: number;

  // Agent conversation tracking
  private conversationStartTime: number = 0;
  private messageCount: number = 0;
  private clarificationCount: number = 0;
  private refinementCount: number = 0;
  private lastQueryTime: number = 0;
  private lastQueryId: string | null = null;
  private chatboxOpenTime: number = 0;
  private currentAgentId: string | null = null;

  constructor() {
    this.sessionStartTime = Date.now();
    this.sessionId = `rb_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.queryStartTime = 0;
    this.reportGeneratedTime = 0;
  }

  // Public getter for sessionId (proper encapsulation)
  public getSessionId(): string {
    return this.sessionId;
  }

  // Funnel Stage 1: View Report Builder
  public trackReportBuilderViewed(referrer?: string) {
    this.track('Report Builder - Page Viewed', {
      referrer: referrer || 'direct',
    });
  }

  // Track when user interacts with example queries
  public trackExampleQueryClicked(exampleQuery: string, index: number, isAISuggestion: boolean = false) {
    this.track('Report Builder - Example Query Clicked', {
      example_query: exampleQuery,
      example_index: index,
      is_ai_suggestion: isAISuggestion,
      time_on_page_seconds: this.getTimeOnPage(),
    });
  }

  // Funnel Stage 2: Ask Question (tracks when user submits query, not when response received)
  public trackQuerySubmitted(queryData: ReportBuilderQueryData) {
    this.queryStartTime = Date.now();

    // Sanitize query text before tracking (Phase 3)
    const sanitizedQuery = this.sanitizeQueryText(queryData.queryText);

    this.track('Report Builder - Query Submitted', {
      query_text: sanitizedQuery,
      query_length: queryData.queryText.length,
      time_on_page_before_query_seconds: this.getTimeOnPage(),
      // Agent integration fields (available at submission time)
      agent_id: queryData.agentId,
      agent_name: queryData.agentName,
      conversation_type: queryData.conversationType,
      message_number: queryData.messageNumber,
      session_id: queryData.sessionId,
    });
  }

  // Funnel Stage 3: View Generated Report
  public trackReportGenerated(success: boolean, reportUrl?: string) {
    this.reportGeneratedTime = Date.now();
    const timeToGenerate = this.queryStartTime ? (Date.now() - this.queryStartTime) / 1000 : 0;

    this.track('Report Builder - Report Generated', {
      success,
      report_url: reportUrl,
      time_to_generate_seconds: timeToGenerate,
    });
  }

  // Track report preview interactions
  public trackReportPreviewInteraction(action: 'viewed' | 'refreshed' | 'error', details?: any) {
    this.track('Report Builder - Preview Interaction', {
      action,
      details,
      time_since_generation_seconds: this.reportGeneratedTime ? (Date.now() - this.reportGeneratedTime) / 1000 : 0,
    });
  }

  // Funnel Stage 4: Save as Custom Report
  public trackSaveReportClicked() {
    this.track('Report Builder - Save Report Clicked', {
      time_since_generation_seconds: this.reportGeneratedTime ? (Date.now() - this.reportGeneratedTime) / 1000 : 0,
    });
  }

  public trackReportSaved(saveData: ReportSaveData) {
    this.track('Report Builder - Custom Report Saved', {
      custom_report_id: saveData.customReportId,
      report_name: saveData.reportName,
      time_to_save_seconds: saveData.timeToSaveSeconds,
      modifications_made: saveData.modificationsMode,
      time_since_generation_seconds: this.reportGeneratedTime ? (Date.now() - this.reportGeneratedTime) / 1000 : 0,
      total_session_time_seconds: this.getTimeOnPage(),
    });

    // Track funnel completion
    this.trackFunnelCompletion();
  }

  // Track when save fails
  public trackSaveReportFailed(errorMessage: string) {
    this.track('Report Builder - Save Report Failed', {
      error_message: errorMessage,
      time_since_generation_seconds: this.reportGeneratedTime ? (Date.now() - this.reportGeneratedTime) / 1000 : 0,
    });
  }

  // Track AI suggestion interactions
  public trackAISuggestionGenerated(suggestions: string[]) {
    this.track('Report Builder - AI Suggestions Generated', {
      suggestions_count: suggestions.length,
      suggestions: suggestions.slice(0, 5), // Track up to 5 suggestions
    });
  }

  // Track refinement requests
  public trackRefinementRequested(originalConfidence: number) {
    this.track('Report Builder - Refinement Requested', {
      original_confidence: originalConfidence,
      confidence_category: this.getConfidenceCategory(originalConfidence),
    });
  }

  // Track user leaving without saving
  public trackSessionAbandoned(stage: 'view' | 'query' | 'report_generated') {
    this.track('Report Builder - Session Abandoned', {
      abandonment_stage: stage,
      total_session_time_seconds: this.getTimeOnPage(),
    });
  }

  // Track errors
  public trackError(errorType: 'query_parse' | 'api_call' | 'report_generation' | 'save',
                    errorMessage: string) {
    this.track('Report Builder - Error Occurred', {
      error_type: errorType,
      error_message: errorMessage,
      time_on_page_seconds: this.getTimeOnPage(),
    });
  }

  // Track report builder feature discovery
  public trackFeatureDiscovery(source: 'menu' | 'dashboard' | 'tutorial' | 'search' | 'other') {
    this.track('Report Builder - Feature Discovered', {
      discovery_source: source,
      is_first_visit: this.isFirstVisit(),
    });
  }

  // Track performance metrics
  public trackPerformanceMetrics(metrics: {
    apiResponseTime: number;
    renderTime: number;
    totalDataRows: number;
  }) {
    this.track('Report Builder - Performance Metrics', {
      api_response_time_ms: metrics.apiResponseTime,
      render_time_ms: metrics.renderTime,
      total_data_rows: metrics.totalDataRows,
      performance_category: this.getPerformanceCategory(metrics.apiResponseTime),
    });
  }

  // ============ Agent Integration Tracking Methods ============

  // Track when agent conversation starts
  public trackAgentConversationStarted(
    agentId: string,
    sessionId: string,
    initiationType: 'auto' | 'manual' | 'page_navigation',
  ) {
    this.conversationStartTime = Date.now();
    this.messageCount = 0;
    this.clarificationCount = 0;
    this.refinementCount = 0;
    this.currentAgentId = agentId;

    this.track('Report Builder - Agent Conversation Started', {
      agent_id: agentId,
      session_id: sessionId,
      initiation_type: initiationType,
    });
  }

  // Track agent conversation quality
  public trackAgentConversationQuality(data: AgentConversationData) {
    const duration = this.conversationStartTime
      ? (Date.now() - this.conversationStartTime) / 1000
      : 0;

    this.track('Report Builder - Agent Conversation Quality', {
      agent_id: data.agentId,
      session_id: data.sessionId,
      message_count: data.messageCount,
      clarification_requests_count: data.clarificationRequestsCount,
      successful_interpretation: data.successfulInterpretation,
      conversation_duration_seconds: duration,
      query_refinement_iterations: data.queryRefinementIterations,
      messages_per_minute: duration > 0 ? (data.messageCount / duration) * 60 : 0,
      quality_score: this.calculateConversationQualityScore(data),
    });
  }

  // Track agent response type
  public trackAgentResponseType(data: AgentResponseData) {
    this.messageCount++;

    if (data.responseType === 'clarification') {
      this.clarificationCount++;
    }

    this.track('Report Builder - Agent Response Type', {
      response_type: data.responseType,
      confidence_score: data.confidenceScore,
      requires_user_action: data.requiresUserAction,
      processing_time_ms: data.processingTimeMs,
      message_number: data.messageNumber,
      response_speed_category: this.getResponseSpeedCategory(data.processingTimeMs),
    });
  }

  // Track query refinement
  public trackQueryRefinement(data: QueryRefinementData) {
    this.refinementCount++;

    const timeSinceLastQuery = this.lastQueryTime
      ? (Date.now() - this.lastQueryTime) / 1000
      : 0;

    // Sanitize queries before tracking (Phase 3)
    const sanitizedOriginal = this.sanitizeQueryText(data.originalQuery);
    const sanitizedRefined = this.sanitizeQueryText(data.refinedQuery);

    this.track('Report Builder - Query Refinement', {
      original_query: sanitizedOriginal,
      refined_query: sanitizedRefined,
      refinement_reason: data.refinementReason,
      iteration_number: data.iterationNumber,
      previous_confidence_score: data.previousConfidenceScore,
      time_to_refinement_seconds: timeSinceLastQuery,
      query_similarity: this.calculateQuerySimilarity(sanitizedOriginal, sanitizedRefined),
    });
  }

  // Track chatbox opened
  public trackChatboxOpened(
    source: 'button' | 'auto_on_page_load' | 'query_submission',
    selectedAgent: string,
  ) {
    this.chatboxOpenTime = Date.now();

    this.track('Report Builder - Chatbox Opened', {
      source,
      selected_agent: selectedAgent,
      time_on_page_before_open_seconds: this.getTimeOnPage(),
    });
  }

  // Track chatbox closed
  public trackChatboxClosed(
    interactionCount: number,
    reportGenerated: boolean,
  ) {
    const duration = this.chatboxOpenTime
      ? (Date.now() - this.chatboxOpenTime) / 1000
      : 0;

    this.track('Report Builder - Chatbox Closed', {
      duration_seconds: duration,
      interaction_count: interactionCount,
      report_generated: reportGenerated,
      abandonment: !reportGenerated,
    });

    this.chatboxOpenTime = 0;
  }

  // Track chatbox tab switch
  public trackChatboxTabSwitch(
    fromTab: 'chat' | 'history' | 'agents',
    toTab: 'chat' | 'history' | 'agents',
    timeOnPreviousTab: number,
  ) {
    this.track('Report Builder - Chatbox Tab Switch', {
      from_tab: fromTab,
      to_tab: toTab,
      time_on_previous_tab_seconds: timeOnPreviousTab,
    });
  }

  // Track agent selected
  public trackAgentSelected(data: AgentSelectionData) {
    const previousAgent = this.currentAgentId;
    this.currentAgentId = data.agentId;

    this.track('Report Builder - Agent Selected', {
      agent_id: data.agentId,
      agent_name: data.agentName,
      selection_method: data.selectionMethod,
      previous_agent: previousAgent || data.previousAgent,
      is_new_conversation: data.isNewConversation,
      is_agent_switch: !!previousAgent && previousAgent !== data.agentId,
    });
  }

  // ============ Helper Methods for Agent Integration ============

  // Calculate conversation quality score (0-100)
  private calculateConversationQualityScore(data: AgentConversationData): number {
    let score = 100;

    // Deduct for excessive clarifications
    score -= Math.min(data.clarificationRequestsCount * 10, 30);

    // Deduct for too many messages (inefficiency)
    if (data.messageCount > 5) {
      score -= Math.min((data.messageCount - 5) * 5, 20);
    }

    // Deduct if unsuccessful
    if (!data.successfulInterpretation) {
      score -= 40;
    }

    // Deduct for excessive time
    if (data.conversationDuration > 120) {
      score -= 10;
    }

    return Math.max(score, 0);
  }

  // Get response speed category
  private getResponseSpeedCategory(ms: number): string {
    if (ms < 1000) { return 'fast'; }
    if (ms < 3000) { return 'normal'; }
    if (ms < 5000) { return 'slow'; }
    return 'very_slow';
  }

  // Calculate query similarity (simple character-based)
  private calculateQuerySimilarity(query1: string, query2: string): number {
    const len1 = query1.length;
    const len2 = query2.length;
    const maxLen = Math.max(len1, len2);

    if (maxLen === 0) { return 1.0; }

    // Simple character-based similarity
    const commonChars = query1.split('').filter((c) => query2.includes(c)).length;
    return commonChars / maxLen;
  }

  // ============ Privacy & Data Sanitization (Phase 3) ============

  /**
   * Sanitize query text to remove PII before tracking
   * Removes: emails, phone numbers, customer names in quotes
   */
  private sanitizeQueryText(query: string): string {
    if (!query) { return query; }

    let sanitized = query;

    // Remove email addresses using shared pattern
    sanitized = sanitized.replace(ReportBuilderEventHandler.EMAIL_PATTERN, '[EMAIL]');

    // Remove phone numbers (various formats) using shared patterns
    // Matches: 123-456-7890, (123) 456-7890, 123.456.7890, 1234567890
    sanitized = sanitized.replace(ReportBuilderEventHandler.PHONE_PATTERN_1, '[PHONE]');
    sanitized = sanitized.replace(ReportBuilderEventHandler.PHONE_PATTERN_2, '[PHONE]');

    // Remove potential customer names in quotes
    sanitized = sanitized.replace(/customer\s+["']([^"']+)["']/gi, 'customer [NAME]');
    sanitized = sanitized.replace(/named?\s+["']([^"']+)["']/gi, 'named [NAME]');
    sanitized = sanitized.replace(/for\s+["']([^"']+)["']/gi, 'for [NAME]');

    // Remove credit card patterns using shared pattern (handles separators)
    sanitized = sanitized.replace(ReportBuilderEventHandler.CREDIT_CARD_PATTERN, '[CARD]');

    // Remove SSN patterns using shared pattern
    sanitized = sanitized.replace(ReportBuilderEventHandler.SSN_PATTERN, '[SSN]');

    return sanitized;
  }

  /**
   * Validate that tracking data doesn't contain obvious PII
   * Returns true if data appears safe to track
   */
  private validateTrackingData(data: any): boolean {
    const dataStr = JSON.stringify(data).toLowerCase();

    // Check for common PII patterns using shared patterns (without 'g' flag for test())
    // Note: Creating new RegExp from source to avoid 'g' flag state issues in test()
    const piiPatterns = [
      new RegExp(ReportBuilderEventHandler.EMAIL_PATTERN.source), // Email addresses
      new RegExp(ReportBuilderEventHandler.SSN_PATTERN.source), // SSN
      new RegExp(ReportBuilderEventHandler.CREDIT_CARD_PATTERN.source), // Credit cards with separators
      new RegExp(ReportBuilderEventHandler.PHONE_PATTERN_1.source), // Phone numbers
      new RegExp(ReportBuilderEventHandler.PHONE_PATTERN_2.source), // Phone numbers (alternate format)
    ];

    for (const pattern of piiPatterns) {
      if (pattern.test(dataStr)) {
        console.warn('[ReportBuilder] Potential PII detected in tracking data, skipping event');
        return false;
      }
    }

    return true;
  }

  private track(event: string, extra: any = {}) {
    try {
      // Check if Mixpanel has been initialized via MixpanelInitializer
      const initStatus = MixpanelInitializer.getInitializationStatus();
      if (!initStatus.isInitialized || !initStatus.distinctId) {
        // Silently skip tracking if not initialized - this is normal during app startup
        return;
      }

      // Phase 3: Validate tracking data doesn't contain PII
      const trackingData = {
        timestamp: new Date().toISOString(),
        session_id: this.sessionId,
        ...extra,
      };

      if (!this.validateTrackingData(trackingData)) {
        console.warn(`[ReportBuilder] Skipping event "${event}" due to potential PII`);
        return;
      }

      mixpanel.track(event, trackingData);
    } catch (err: any) {
      console.error('Report Builder track event error: ', err);
    }
  }

  // Track detailed query parameters for deeper analysis
  private trackQueryParameters(queryData: ReportBuilderQueryData) {
    this.track('Report Builder - Query Parameters', {
      measure: queryData.measure,
      dimension: queryData.dimension,
      filters: queryData.filters?.map((f) => ({
        field: f.code || f.column,
        operator: f.operator,
        has_value: !!f.value,
        value_count: Array.isArray(f.value) ? f.value.length : 1,
      })) || [],
      columns: queryData.columns,
      date_range: queryData.dateRange,
      confidence_score: queryData.confidenceScore,
      confidence_category: queryData.confidenceScore !== undefined
        ? this.getConfidenceCategory(queryData.confidenceScore)
        : 'unknown',
    });
  }

  // Track funnel completion
  private trackFunnelCompletion() {
    const totalTime = this.getTimeOnPage();
    const stages = {
      view_to_query: this.queryStartTime ? (this.queryStartTime - this.sessionStartTime) / 1000 : 0,
      query_to_report: this.reportGeneratedTime && this.queryStartTime ?
        (this.reportGeneratedTime - this.queryStartTime) / 1000 : 0,
      report_to_save: this.reportGeneratedTime ?
        (Date.now() - this.reportGeneratedTime) / 1000 : 0,
    };

    this.track('Report Builder - Funnel Completed', {
      total_time_seconds: totalTime,
      stage_times: stages,
    });
  }

  // Helper methods
  private getTimeOnPage(): number {
    return (Date.now() - this.sessionStartTime) / 1000;
  }

  private getConfidenceCategory(score: number): string {
    if (score >= 0.8) { return 'high'; }
    if (score >= 0.5) { return 'medium'; }
    return 'low';
  }

  private isFirstVisit(): boolean {
    // Check if user has visited Report Builder before
    const hasVisited = localStorage.getItem('rb_has_visited');
    if (!hasVisited) {
      localStorage.setItem('rb_has_visited', 'true');
      return true;
    }
    return false;
  }

  private getPerformanceCategory(responseTime: number): string {
    if (responseTime < 1000) { return 'fast'; }
    if (responseTime < 3000) { return 'normal'; }
    return 'slow';
  }
}
